import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarDays, ChefHat, IdCard, Plus, Printer, Sparkles, Trash2, UserPlus, Wallet } from 'lucide-react';
import {
  addDays,
  formatMoney,
  shopDate,
  type Employee,
  type PayItem,
  type ShiftDayType,
  type WorkShift,
} from '@bakery/shared';

import { api } from '../lib/api.js';
import { useAuth } from '../lib/auth.js';
import { PositionsModal } from './Positions.js';
import { printPayslip } from '../lib/printing.js';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  Field,
  Input,
  Modal,
  MoneyInput,
  SectionHeading,
  Select,
  Skeleton,
  Tabs,
  Textarea,
  cx,
  useToast,
} from '../components/ui.js';

type EmployeeWithPay = Employee & { payItems: PayItem[] };

export function StaffScreen() {
  const { can } = useAuth();
  const [tab, setTab] = useState<'team' | 'roster' | 'payroll'>('team');
  const [addOpen, setAddOpen] = useState(false);

  const [positionsOpen, setPositionsOpen] = useState(false);

  /*
   * Former staff are fetched with everyone else and split below. Their records
   * are kept — payslips name them, and "who was on that week" outlives the
   * person — so the Team tab has a view for them rather than pretending they
   * were never here.
   */
  const employees = useQuery({ queryKey: ['employees', 'all'], queryFn: () => api.staff.employees(true) });
  const active = (employees.data ?? []).filter((employee) => employee.isActive);

  // The payslip builder issues pay for anyone, so it is payroll-only. Someone
  // with just "payroll.own" sees their own slips on their own record instead.
  const canRunPayroll = can('payroll.all');
  const canEditTeam = can('staff.edit');

  return (
    <div className="mx-auto max-w-5xl px-4 py-5">
      <header className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-semibold">Team &amp; payroll</h1>
          <p className="mt-0.5 text-sm text-[var(--color-ink-soft)]">
            Staff, their rates, the roster, and printable payslips.
          </p>
        </div>
        {canEditTeam && (
          <div className="flex flex-wrap gap-2">
            <Button icon={<IdCard className="size-4" />} onClick={() => setPositionsOpen(true)}>
              Positions
            </Button>
            <Button variant="primary" icon={<UserPlus className="size-4" />} onClick={() => setAddOpen(true)}>
              Add employee
            </Button>
          </div>
        )}
      </header>

      <div className="mb-4">
        <Tabs
          value={tab}
          onChange={setTab}
          tabs={[
            { value: 'team' as const, label: 'Team', count: active.length },
            { value: 'roster' as const, label: 'Roster' },
            ...(canRunPayroll ? [{ value: 'payroll' as const, label: 'Payslips' }] : []),
          ]}
        />
      </div>

      {tab === 'team' && <TeamTab employees={employees.data ?? []} loading={employees.isLoading} />}
      {/* The rota and the payslip builder are about people who are still here. */}
      {tab === 'roster' && <RosterTab employees={active} />}
      {tab === 'payroll' && canRunPayroll && <PayrollTab employees={active} />}

      {addOpen && <EmployeeModal onClose={() => setAddOpen(false)} />}
      {positionsOpen && (
        <PositionsModal onClose={() => setPositionsOpen(false)} />
      )}
    </div>
  );
}

/**
 * The three statutory contributions every Philippine payslip carries.
 *
 * Labels only. Each agency sets its own contribution table and revises it by
 * circular, so a figure computed here would be quietly wrong the month after
 * one changed — and a wrong deduction is the employee's money.
 */
const STATUTORY_DEDUCTIONS = ['SSS', 'PhilHealth', 'Pag-IBIG'] as const;

const PAY_TYPE_LABEL: Record<string, string> = {
  monthly: 'Monthly salary',
  daily: 'Daily rate',
  hourly: 'Hourly',
  per_shift: 'Per shift',
  piece_rate: 'Per piece',
};

/**
 * What somebody is paid, in one phrase.
 *
 * Only the rate their pay type actually uses. An employee row carries all four
 * rate columns whatever their pay type, so reading the wrong one shows a
 * confident ₱0 — and the monthly figure was the only one ever shown, which
 * left everybody on a daily rate with no pay on their card at all.
 */
function payLine(employee: Employee): string | null {
  const rate = {
    monthly: employee.monthlySalary,
    daily: employee.dailyRate,
    hourly: employee.hourlyRate,
    per_shift: employee.shiftRate,
    piece_rate: null,
  }[employee.payType];

  if (rate === null || rate === undefined) return null;
  const per = { monthly: 'month', daily: 'day', hourly: 'hour', per_shift: 'shift' }[
    employee.payType as 'monthly' | 'daily' | 'hourly' | 'per_shift'
  ];
  return `${formatMoney(rate)} / ${per}`;
}

function TeamTab({ employees, loading }: { employees: EmployeeWithPay[]; loading: boolean }) {
  const [editing, setEditing] = useState<EmployeeWithPay | null>(null);
  const [showing, setShowing] = useState<'here' | 'former'>('here');

  const here = employees.filter((employee) => employee.isActive);
  const former = employees.filter((employee) => !employee.isActive);

  /*
   * The toggle only appears once somebody has left, so putting the last of
   * them back would otherwise strand you on an empty "Nobody has left" pane
   * with nothing on screen to click your way out of.
   */
  const viewing = former.length === 0 ? 'here' : showing;
  const shown = viewing === 'here' ? here : former;

  if (loading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 3 }).map((_, index) => (
          <Skeleton key={index} className="h-20" />
        ))}
      </div>
    );
  }

  if (employees.length === 0) {
    return <EmptyState icon={<ChefHat className="size-8" />} title="No staff yet" description="Add your bakers, cashiers and riders to start tracking shifts and pay." />;
  }

  return (
    <>
      {/*
        * Only offered once somebody has actually left. A tab that is always
        * empty is a tab that teaches people to ignore it.
        */}
      {former.length > 0 && (
        <div className="mb-3 flex gap-2">
          {(
            [
              ['here', `On the team (${here.length})`],
              ['former', `Former (${former.length})`],
            ] as const
          ).map(([value, label]) => (
            <button
              key={value}
              onClick={() => setShowing(value)}
              className={cx(
                'rounded-[var(--radius-md)] px-3 py-1.5 text-sm font-medium transition',
                viewing === value
                  ? 'bg-[var(--color-accent-soft)] text-[var(--color-accent-ink)]'
                  : 'text-[var(--color-ink-soft)] hover:bg-[var(--color-surface-sunken)]',
              )}
            >
              {label}
            </button>
          ))}
        </div>
      )}

      {shown.length === 0 ? (
        <EmptyState
          icon={<ChefHat className="size-8" />}
          title={viewing === 'former' ? 'Nobody has left' : 'Nobody on the team'}
        />
      ) : (
        <ul className="space-y-2">
          {shown.map((employee) => (
            <li key={employee.id}>
              <button
                onClick={() => setEditing(employee)}
                className={cx(
                  'card flex w-full items-center gap-4 p-4 text-left transition hover:border-crust-400',
                  !employee.isActive && 'opacity-70',
                )}
              >
                <span className="grid size-10 shrink-0 place-items-center rounded-full bg-[var(--color-accent-soft)] font-display text-sm font-semibold text-[var(--color-accent-ink)]">
                  {employee.firstName.slice(0, 1)}
                  {employee.lastName.slice(0, 1)}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{employee.displayName}</p>
                  <p className="truncate text-xs text-[var(--color-ink-soft)]">
                    {employee.employeeNo} · {PAY_TYPE_LABEL[employee.payType]}
                    {payLine(employee) && ` · ${payLine(employee)}`}
                  </p>
                  {!employee.isActive && (
                    <p className="truncate text-xs text-[var(--color-ink-faint)]">
                      Left {employee.endedOn ?? 'the shop'}
                      {employee.leavingReason && ` — ${employee.leavingReason}`}
                    </p>
                  )}
                </div>
                <div className="flex shrink-0 flex-wrap justify-end gap-1">
                  {/*
                    * Piece rates belong to people paid by the piece. Showing
                    * them for anyone else is how a baker moved onto a daily
                    * wage still reads as being paid per loaf.
                    */}
                  {employee.payType === 'piece_rate' &&
                    employee.payItems
                      .filter((item) => item.isActive)
                      .map((item) => (
                        <Badge key={item.id} tone="brand">
                          {item.label} {formatMoney(item.rate, { compactWhole: true })}
                        </Badge>
                      ))}
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}
      {editing && <EmployeeModal employee={editing} onClose={() => setEditing(null)} />}
    </>
  );
}

function RosterTab({ employees }: { employees: EmployeeWithPay[] }) {
  const queryClient = useQueryClient();
  const [weekStart, setWeekStart] = useState(() => shopDate());
  const [adding, setAdding] = useState<{ employeeId: string; date: string } | null>(null);

  const days = useMemo(() => Array.from({ length: 7 }, (_, index) => addDays(weekStart, index)), [weekStart]);

  const shifts = useQuery({
    queryKey: ['work-shifts', weekStart],
    queryFn: () =>
      api.staff.shifts({ from: `${weekStart}T00:00:00.000Z`, to: `${addDays(weekStart, 7)}T00:00:00.000Z` }),
  });

  const [editing, setEditing] = useState<WorkShift | null>(null);

  return (
    <Card className="overflow-x-auto p-4">
      <SectionHeading
        title="Weekly roster"
        hint="Tap an empty cell to schedule someone, or a shift to change it. Every shift rostered here counts as a day worked on the payslip unless you mark it absent."
        action={
          <div className="flex gap-1">
            <Button size="sm" onClick={() => setWeekStart(addDays(weekStart, -7))}>Previous</Button>
            <Button size="sm" onClick={() => setWeekStart(shopDate())}>This week</Button>
            <Button size="sm" onClick={() => setWeekStart(addDays(weekStart, 7))}>Next</Button>
          </div>
        }
      />

      <table className="w-full min-w-[720px] text-sm">
        <thead>
          <tr className="text-left text-xs text-[var(--color-ink-soft)]">
            <th className="w-44 pb-2 font-medium">Employee</th>
            {days.map((day) => (
              <th key={day} className="pb-2 text-center font-medium">
                {new Date(`${day}T00:00:00Z`).toLocaleDateString('en-PH', { weekday: 'short', day: 'numeric', timeZone: 'UTC' })}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-[var(--color-line)]">
          {employees.map((employee) => (
            <tr key={employee.id}>
              <td className="py-2 pr-2 text-sm font-medium">{employee.displayName}</td>
              {days.map((day) => {
                const dayShifts = (shifts.data ?? []).filter(
                  (shift) => shift.employeeId === employee.id && shift.startsAt.slice(0, 10) === day,
                );
                return (
                  <td key={day} className="px-1 py-1.5 align-top">
                    {dayShifts.length === 0 ? (
                      <button
                        onClick={() => setAdding({ employeeId: employee.id, date: day })}
                        className="h-11 w-full rounded-[var(--radius-sm)] border border-dashed border-[var(--color-line-strong)] text-[var(--color-ink-faint)] transition hover:border-crust-400 hover:text-crust-600"
                        aria-label={`Schedule ${employee.displayName} on ${day}`}
                      >
                        <Plus className="mx-auto size-3.5" />
                      </button>
                    ) : (
                      dayShifts.map((shift) => (
                        <button
                          key={shift.id}
                          onClick={() => setEditing(shift)}
                          title="Click to change this shift"
                          className={cx(
                            'mb-1 block w-full rounded-[var(--radius-sm)] px-2 py-1.5 text-[11px] leading-tight transition hover:brightness-95',
                            shift.status === 'absent'
                              ? 'bg-[var(--color-danger-soft)] text-[var(--color-danger)] line-through'
                              : shift.dayType !== 'regular'
                                ? 'bg-[var(--color-warn-soft)] text-[var(--color-warn)]'
                                : 'bg-[var(--color-accent-soft)] text-[var(--color-accent-ink)]',
                          )}
                        >
                          {shift.label ?? 'Shift'}
                          <span className="block opacity-70">
                            {shift.startsAt.slice(11, 16)}–{shift.endsAt.slice(11, 16)}
                          </span>
                          {/* Only the exceptions are worth the width. */}
                          {shift.status === 'absent' && <span className="block font-medium">Absent</span>}
                          {shift.dayType !== 'regular' && (
                            <span className="block font-medium">
                              {shift.dayType === 'regular_holiday' ? 'Holiday' : 'Special day'}
                            </span>
                          )}
                        </button>
                      ))
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>

      {adding && (
        <ShiftModal
          employee={employees.find((e) => e.id === adding.employeeId)!}
          date={adding.date}
          onClose={() => setAdding(null)}
        />
      )}
      {editing && <ShiftModal shift={editing} onClose={() => setEditing(null)} />}
    </Card>
  );
}

/**
 * What a shift opens on for somebody with no hours recorded yet.
 *
 * A last resort, not a policy: everyone who works here should have their own
 * hours on their card. It exists so a brand-new employee's first shift starts
 * from a working day rather than an empty pair of time fields.
 */
const FALLBACK_SHIFT = { start: '09:00', end: '18:00' };

const DAY_TYPES: { value: ShiftDayType; label: string }[] = [
  { value: 'regular', label: 'Ordinary day' },
  { value: 'regular_holiday', label: 'Regular holiday (pays double)' },
  { value: 'special_non_working', label: 'Special non-working day (pays 130%)' },
];

/**
 * Scheduling a shift, and afterwards saying what became of it.
 *
 * One dialog for both, because a shift is one thing whose details are filled
 * in over time: the hours when the rota is drawn up, whether it was a holiday
 * as soon as that is known, and an absence only if it happens.
 */
function ShiftModal({
  employee,
  date,
  shift,
  onClose,
}: {
  employee?: EmployeeWithPay;
  date?: string;
  shift?: WorkShift;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const day = shift ? shift.startsAt.slice(0, 10) : date!;

  /*
   * Prefilled from the hours this person actually works. It used to open on
   * 05:00–13:00 "Morning bake" for everybody, which is nobody's shift here —
   * so every cell on the rota had to be corrected before it was right.
   *
   * `||` rather than `??`, deliberately. An employee saved without their hours
   * filled in holds an empty string, not null, and `??` would have handed that
   * straight to the time input — opening the rota on a blank time, which is
   * worse than opening it on the wrong one.
   */
  const [label, setLabel] = useState(shift?.label ?? 'Shift');
  const [start, setStart] = useState(
    shift?.startsAt.slice(11, 16) || employee?.defaultShiftStart || FALLBACK_SHIFT.start,
  );
  const [end, setEnd] = useState(
    shift?.endsAt.slice(11, 16) || employee?.defaultShiftEnd || FALLBACK_SHIFT.end,
  );
  const [dayType, setDayType] = useState<ShiftDayType>(shift?.dayType ?? 'regular');
  const [absent, setAbsent] = useState(shift?.status === 'absent');
  const [absenceReason, setAbsenceReason] = useState(shift?.absenceReason ?? '');

  const done = () => {
    queryClient.invalidateQueries({ queryKey: ['work-shifts'] });
    // The payslip is built from these, so it has to be re-read alongside.
    queryClient.invalidateQueries({ queryKey: ['payslip-draft'] });
    onClose();
  };

  const body = {
    // Manila is UTC+8 year round, so the offset can be written directly.
    startsAt: `${day}T${start}:00.000+08:00`,
    endsAt: `${day}T${end}:00.000+08:00`,
    label,
    dayType,
    status: absent ? 'absent' : shift && shift.status !== 'absent' ? shift.status : 'scheduled',
    absenceReason: absent ? absenceReason.trim() || null : null,
  };

  const save = useMutation({
    mutationFn: () =>
      shift
        ? api.staff.updateShift(shift.id, body)
        : api.staff.createShift({ employeeId: employee!.id, ...body }),
    onSuccess: done,
    onError: (error: Error) => toast.push({ tone: 'danger', title: 'Could not save', body: error.message }),
  });

  const remove = useMutation({
    mutationFn: () => api.staff.deleteShift(shift!.id),
    onSuccess: done,
  });

  return (
    <Modal
      open
      onClose={onClose}
      size="sm"
      title={shift ? 'This shift' : 'Schedule a shift'}
      description={shift ? undefined : employee?.displayName}
      footer={
        <>
          {shift && (
            <Button
              variant="ghost"
              className="text-[var(--color-danger)]"
              loading={remove.isPending}
              onClick={() => remove.mutate()}
            >
              Remove
            </Button>
          )}
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={save.isPending} onClick={() => save.mutate()}>
            {shift ? 'Save' : 'Schedule'}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Field label="What for">
          <Input value={label} onChange={(event) => setLabel(event.target.value)} />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Starts">
            <Input type="time" value={start} onChange={(event) => setStart(event.target.value)} />
          </Field>
          <Field label="Ends">
            <Input type="time" value={end} onChange={(event) => setEnd(event.target.value)} />
          </Field>
        </div>

        <Field label="Kind of day" hint="Holiday premiums are worked out from this.">
          <Select value={dayType} onChange={(event) => setDayType(event.target.value as ShiftDayType)}>
            {DAY_TYPES.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </Select>
        </Field>

        {/*
          * The only attendance the shop has to key in. A rostered shift counts
          * as worked, so this is the exception — which is how the paper slip
          * reads too: days worked, then shifts absent, then the reason.
          */}
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={absent}
            onChange={(event) => setAbsent(event.target.checked)}
            className="size-4 accent-[var(--color-danger)]"
          />
          They did not work this shift
        </label>

        {absent && (
          <Field label="Reason" hint="Printed on the payslip beside the absence.">
            <Input
              value={absenceReason}
              onChange={(event) => setAbsenceReason(event.target.value)}
              placeholder="Sick, personal, no schedule…"
            />
          </Field>
        )}
      </div>
    </Modal>
  );
}

/**
 * The payslip builder. It mirrors the bakery's paper slip: a daily grid per pay
 * item, totals, named adjustments, then a grand total — computed, previewed,
 * and only then issued.
 */
function PayrollTab({ employees }: { employees: EmployeeWithPay[] }) {
  const queryClient = useQueryClient();
  const toast = useToast();

  const today = shopDate();
  const [employeeId, setEmployeeId] = useState(employees[0]?.id ?? '');
  const [periodStart, setPeriodStart] = useState(() => addDays(today, -14));
  const [periodEnd, setPeriodEnd] = useState(today);
  const [adjustments, setAdjustments] = useState<{ label: string; amount: number; kind: 'addition' | 'deduction' }[]>([]);

  const employee = employees.find((e) => e.id === employeeId) ?? employees[0];

  const logs = useQuery({
    queryKey: ['work-logs', employeeId, periodStart, periodEnd],
    queryFn: () => api.staff.workLogs(employeeId, periodStart, periodEnd),
    enabled: Boolean(employeeId),
  });

  /*
   * The adjustments are deliberately NOT part of the key.
   *
   * They were, and every keystroke in an adjustment's label therefore made a
   * new query: React Query flipped to `isLoading`, the whole table was
   * replaced by a skeleton, and the input being typed into was unmounted. The
   * effect at the keyboard was losing focus after each letter and having to
   * click back into the field — which is exactly what the shop reported.
   *
   * Nothing about the lines depends on an adjustment anyway. They are named
   * sums added to or taken off the end, so they are applied below, in the
   * browser, where they cost nothing and appear as they are typed.
   */
  const draft = useQuery({
    queryKey: ['payslip-draft', employeeId, periodStart, periodEnd],
    queryFn: () => api.staff.draftPayslip({ employeeId, periodStart, periodEnd }),
    enabled: Boolean(employeeId),
    // One more guard on the same problem: changing the period keeps the old
    // table on screen instead of collapsing it to a skeleton.
    placeholderData: (previous) => previous,
  });

  const roles = useQuery({ queryKey: ['roles'], queryFn: api.staff.roles });
  const roleName = roles.data?.find((role) => role.id === employee?.roleId)?.name ?? null;

  /*
   * The last slip this person was issued, read only to offer the statutory
   * amounts again. They are the same figure period after period until an
   * agency changes its table, so retyping them every fortnight is pure
   * transcription — and transcription is where a wrong deduction comes from.
   */
  const previous = useQuery({
    queryKey: ['payslips', 'previous', employeeId],
    queryFn: async () => {
      const slips = await api.staff.payslips(employeeId);
      return slips.length > 0 ? api.staff.payslip(slips[0]!.id) : null;
    },
    enabled: Boolean(employeeId),
  });

  const lastAmountFor = (label: string) =>
    previous.data?.adjustments.find((a) => a.label === label && a.kind === 'deduction')?.amount ?? 0;

  const subtotal = draft.data?.draft.subtotal ?? 0;
  const additions = adjustments
    .filter((a) => a.kind === 'addition')
    .reduce((sum, a) => sum + a.amount, 0);
  const deductions = adjustments
    .filter((a) => a.kind === 'deduction')
    .reduce((sum, a) => sum + a.amount, 0);
  const grandTotal = subtotal + additions - deductions;

  const saveLog = useMutation({
    mutationFn: (entries: { payItemId: string; workDate: string; qty: number }[]) =>
      api.staff.saveWorkLogs(employeeId, entries),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['work-logs'] });
      queryClient.invalidateQueries({ queryKey: ['payslip-draft'] });
    },
  });

  const autoFill = useMutation({
    mutationFn: () => api.staff.autoFill(employeeId, periodStart, periodEnd),
    onSuccess: (filled) => {
      queryClient.invalidateQueries({ queryKey: ['work-logs'] });
      queryClient.invalidateQueries({ queryKey: ['payslip-draft'] });
      toast.push({
        tone: filled.length > 0 ? 'ok' : 'neutral',
        title: filled.length > 0 ? `Filled ${filled.length} day${filled.length === 1 ? '' : 's'}` : 'Nothing to fill',
        body: filled.length === 0 ? 'No production runs or deliveries recorded in this period.' : undefined,
      });
    },
  });

  const issue = useMutation({
    mutationFn: () => api.staff.issuePayslip({ employeeId, periodStart, periodEnd, adjustments }),
    onSuccess: (payslip) => {
      queryClient.invalidateQueries({ queryKey: ['payslips'] });
      toast.push({ tone: 'ok', title: `${payslip.payslipNo} issued`, body: formatMoney(payslip.grandTotal) });
    },
  });

  const dates = useMemo(() => {
    const out: string[] = [];
    for (let date = periodStart; date <= periodEnd; date = addDays(date, 1)) out.push(date);
    return out;
  }, [periodStart, periodEnd]);

  if (!employee) {
    return <EmptyState icon={<Wallet className="size-8" />} title="Add an employee first" />;
  }

  /*
   * The daily counts grid is how piece-rate pay is worked out, and nothing
   * else. `computePayslip` has always ignored these counts for everyone
   * else — a daily-rate payslip is days worked times the daily rate — so
   * showing the grid to them was asking for numbers that would never be paid.
   */
  const payItems =
    employee.payType === 'piece_rate' ? employee.payItems.filter((item) => item.isActive) : [];
  const qtyFor = (payItemId: string, date: string) =>
    logs.data?.find((log) => log.payItemId === payItemId && log.workDate === date)?.qty ?? '';

  return (
    <div className="space-y-4">
      <Card className="p-4">
        <div className="grid gap-3 sm:grid-cols-3">
          <Field label="Employee">
            <Select value={employeeId} onChange={(event) => setEmployeeId(event.target.value)}>
              {employees.map((option) => (
                <option key={option.id} value={option.id}>{option.displayName}</option>
              ))}
            </Select>
          </Field>
          <Field label="Period start">
            <Input type="date" value={periodStart} onChange={(event) => setPeriodStart(event.target.value)} />
          </Field>
          <Field label="Period end">
            <Input type="date" value={periodEnd} onChange={(event) => setPeriodEnd(event.target.value)} />
          </Field>
        </div>
      </Card>

      {payItems.length > 0 && (
        <Card className="overflow-x-auto p-4">
          <SectionHeading
            title="Daily counts"
            hint="What this person produced or delivered each day."
            action={
              <Button size="sm" icon={<Sparkles className="size-4" />} loading={autoFill.isPending} onClick={() => autoFill.mutate()}>
                Fill from records
              </Button>
            }
          />

          <table className="w-full min-w-[420px] text-sm">
            <thead>
              <tr className="text-xs text-[var(--color-ink-soft)]">
                <th className="pb-2 text-left font-medium">Date</th>
                {payItems.map((item) => (
                  <th key={item.id} className="pb-2 text-center font-medium">
                    {item.label}
                    <span className="block font-normal opacity-70">{formatMoney(item.rate)} / {item.unitLabel.replace(/s$/, '')}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--color-line)]">
              {dates.map((date) => (
                <tr key={date}>
                  <td className="py-1 text-xs text-[var(--color-ink-soft)]">
                    {new Date(`${date}T00:00:00Z`).toLocaleDateString('en-PH', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' })}
                  </td>
                  {payItems.map((item) => (
                    <td key={item.id} className="px-1 py-1">
                      <input
                        type="number"
                        inputMode="numeric"
                        defaultValue={qtyFor(item.id, date)}
                        onBlur={(event) => {
                          const qty = Number(event.target.value);
                          if (Number.isFinite(qty)) saveLog.mutate([{ payItemId: item.id, workDate: date, qty }]);
                        }}
                        className="tabular h-9 w-full rounded-[var(--radius-sm)] border border-[var(--color-line)] bg-transparent px-2 text-center text-sm focus:border-crust-500 focus:outline-none"
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      <Card className="p-4">
        <SectionHeading title="Payslip" hint={`${periodStart} to ${periodEnd}`} />

        {draft.isLoading ? (
          <Skeleton className="h-32" />
        ) : (
          <>
            <table className="w-full text-sm">
              <tbody className="divide-y divide-[var(--color-line)]">
                {draft.data?.draft.lines.map((line) => (
                  <tr key={line.label}>
                    <td className="py-2">{line.label}</td>
                    <td className="tabular py-2 text-right text-[var(--color-ink-soft)]">
                      {line.qty} {line.unitLabel} × {formatMoney(line.rate)}
                    </td>
                    <td className="tabular py-2 pl-4 text-right font-medium">{formatMoney(line.amount)}</td>
                  </tr>
                ))}

                {adjustments.map((adjustment, index) => (
                  <tr key={index}>
                    <td className="py-2" colSpan={2}>
                      <div className="flex items-center gap-2">
                        <Input
                          value={adjustment.label}
                          onChange={(event) =>
                            setAdjustments((current) => current.map((a, i) => (i === index ? { ...a, label: event.target.value } : a)))
                          }
                          className="h-9 max-w-56"
                        />
                        <Select
                          value={adjustment.kind}
                          onChange={(event) =>
                            setAdjustments((current) =>
                              current.map((a, i) => (i === index ? { ...a, kind: event.target.value as 'addition' | 'deduction' } : a)),
                            )
                          }
                          className="h-9 w-32"
                        >
                          <option value="addition">Add</option>
                          <option value="deduction">Deduct</option>
                        </Select>
                        <button onClick={() => setAdjustments((current) => current.filter((_, i) => i !== index))} aria-label="Remove">
                          <Trash2 className="size-4 text-[var(--color-ink-faint)] hover:text-[var(--color-danger)]" />
                        </button>
                      </div>
                    </td>
                    <td className="py-2 pl-4">
                      <MoneyInput
                        value={adjustment.amount}
                        onChange={(amount) => setAdjustments((current) => current.map((a, i) => (i === index ? { ...a, amount } : a)))}
                        className="h-9"
                      />
                    </td>
                  </tr>
                ))}

                {(additions > 0 || deductions > 0) && (
                  <tr className="text-[var(--color-ink-soft)]">
                    <td className="py-2" colSpan={2}>Gross pay</td>
                    <td className="tabular py-2 pl-4 text-right">{formatMoney(subtotal)}</td>
                  </tr>
                )}

                <tr className="text-base font-semibold">
                  <td className="py-3" colSpan={2}>{deductions > 0 ? 'Net pay' : 'Grand total'}</td>
                  <td className="tabular py-3 pl-4 text-right font-display text-xl text-crust-700">
                    {formatMoney(grandTotal)}
                  </td>
                </tr>
              </tbody>
            </table>

            <div className="mt-3 flex flex-wrap items-center gap-2">
              <Button
                size="sm"
                icon={<Plus className="size-4" />}
                onClick={() => setAdjustments((current) => [...current, { label: 'Additional sales', amount: 0, kind: 'addition' }])}
              >
                Add a line
              </Button>

              {/*
                * The three that come off almost every Philippine payslip.
                *
                * Buttons rather than automatic amounts: the contribution
                * tables are set by each agency and change, so a figure this
                * app worked out for itself would be confidently wrong the
                * month after a circular. The label and the placing are the
                * repetitive part; the amount is a number the shop already has.
                *
                * The last amount used for that person is offered, because
                * these barely move between periods.
                */}
              {STATUTORY_DEDUCTIONS.map((label) => (
                <Button
                  key={label}
                  size="sm"
                  disabled={adjustments.some((a) => a.label === label)}
                  onClick={() =>
                    setAdjustments((current) => [
                      ...current,
                      { label, amount: lastAmountFor(label), kind: 'deduction' },
                    ])
                  }
                >
                  − {label}
                </Button>
              ))}

              <div className="flex-1" />
              <Button
                icon={<Printer className="size-4" />}
                disabled={!draft.data}
                onClick={() =>
                  draft.data &&
                  printPayslip({
                    business: draft.data.business,
                    employeeName: draft.data.employee.displayName,
                    position: roleName,
                    issuedOn: new Date().toISOString(),
                    periodStart,
                    periodEnd,
                    lines: draft.data.draft.lines,
                    adjustments,
                    subtotal,
                    grandTotal,
                  })
                }
              >
                Print
              </Button>
              <Button variant="primary" loading={issue.isPending} onClick={() => issue.mutate()}>
                Issue payslip
              </Button>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}

function EmployeeModal({ employee, onClose }: { employee?: EmployeeWithPay; onClose: () => void }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const roles = useQuery({ queryKey: ['roles'], queryFn: api.staff.roles });

  const [positionsOpen, setPositionsOpen] = useState(false);
  const [leaving, setLeaving] = useState(false);

  const [form, setForm] = useState({
    firstName: employee?.firstName ?? '',
    middleName: employee?.middleName ?? '',
    lastName: employee?.lastName ?? '',
    roleId: employee?.roleId ?? 'role-cashier',
    phone: employee?.phone ?? '',
    address: employee?.address ?? '',
    emergencyContactName: employee?.emergencyContactName ?? '',
    emergencyContact: employee?.emergencyContact ?? '',
    defaultShiftStart: employee?.defaultShiftStart ?? '',
    defaultShiftEnd: employee?.defaultShiftEnd ?? '',
    payType: employee?.payType ?? ('monthly' as Employee['payType']),
    monthlySalary: employee?.monthlySalary ?? 0,
    shiftRate: employee?.shiftRate ?? 0,
    dailyRate: employee?.dailyRate ?? 0,
    hourlyRate: employee?.hourlyRate ?? 0,
  });

  // `id` is absent on a rate that has just been typed in and does not exist yet.
  const [payItems, setPayItems] = useState<
    { id?: string; label: string; unitLabel: string; rate: number }[]
  >(
    employee?.payItems
      .filter((item) => item.isActive)
      .map((item) => ({ id: item.id, label: item.label, unitLabel: item.unitLabel, rate: item.rate })) ?? [],
  );

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['employees'] });

  const save = useMutation({
    mutationFn: async () => {
      /*
       * The rates go with the rest of the form as one list, so a rate taken
       * off the form is actually removed. Saving them one at a time afterwards
       * could only ever add and update — which is why somebody moved onto a
       * daily wage kept their piece rates for months.
       *
       * Sent only for piece-rate staff: on any other pay type they are not
       * shown, and an empty list would silently retire rates that should keep
       * paying if the shop moves them back.
       */
      const body = {
        ...form,
        /*
         * An untouched time field is "not recorded", not "the empty time".
         * Storing the empty string made the rota open on a blank time for
         * anyone whose card had been saved without their hours filled in —
         * including everybody whose card was edited for some other reason.
         */
        defaultShiftStart: form.defaultShiftStart || null,
        defaultShiftEnd: form.defaultShiftEnd || null,
        ...(form.payType === 'piece_rate' ? { payItems } : {}),
      };
      return employee ? api.staff.update(employee.id, body) : api.staff.create(body);
    },
    onSuccess: (saved) => {
      refresh();
      toast.push({ tone: 'ok', title: `${saved.displayName} saved` });
      onClose();
    },
    onError: (error: Error) => toast.push({ tone: 'danger', title: 'Could not save', body: error.message }),
  });

  const reinstate = useMutation({
    mutationFn: () => api.staff.reinstate(employee!.id),
    onSuccess: (saved) => {
      refresh();
      toast.push({ tone: 'ok', title: `${saved.displayName} is back on the team` });
      onClose();
    },
    onError: (error: Error) => toast.push({ tone: 'danger', title: 'Could not do that', body: error.message }),
  });

  return (
    <Modal
      open
      onClose={onClose}
      title={employee ? employee.displayName : 'New employee'}
      description={
        employee && !employee.isActive
          ? `Left ${employee.endedOn ?? 'the shop'}${employee.leavingReason ? ` — ${employee.leavingReason}` : ''}. Their record is kept.`
          : undefined
      }
      footer={
        <>
          {employee &&
            (employee.isActive ? (
              <Button variant="ghost" className="text-[var(--color-danger)]" onClick={() => setLeaving(true)}>
                They have left
              </Button>
            ) : (
              <Button variant="ghost" loading={reinstate.isPending} onClick={() => reinstate.mutate()}>
                Back on the team
              </Button>
            ))}
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={save.isPending} disabled={!form.firstName || !form.lastName} onClick={() => save.mutate()}>
            Save
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-3">
          <Field label="First name" required>
            <Input value={form.firstName} onChange={(event) => setForm({ ...form, firstName: event.target.value })} />
          </Field>
          <Field label="Middle name">
            <Input value={form.middleName} onChange={(event) => setForm({ ...form, middleName: event.target.value })} />
          </Field>
          <Field label="Last name" required>
            <Input value={form.lastName} onChange={(event) => setForm({ ...form, lastName: event.target.value })} />
          </Field>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Position">
            <div className="flex gap-2">
              <Select
                value={form.roleId}
                onChange={(event) => setForm({ ...form, roleId: event.target.value })}
                className="min-w-0 flex-1"
              >
                {roles.data?.map((role) => (
                  <option key={role.id} value={role.id}>{role.name}</option>
                ))}
              </Select>
              {/*
                * Hiring somebody whose job is not on the list is exactly when
                * you need to add it, so the way to do that is here rather than
                * somewhere you have to go and find first.
                */}
              <Button
                className="shrink-0 px-2.5"
                aria-label="Add or rename positions"
                title="Add or rename positions"
                onClick={() => setPositionsOpen(true)}
                icon={<Plus className="size-4" />}
              />
            </div>
          </Field>
          <Field label="Phone">
            <Input value={form.phone} onChange={(event) => setForm({ ...form, phone: event.target.value })} inputMode="tel" />
          </Field>
        </div>

        <Field label="Address">
          <Textarea
            rows={2}
            value={form.address}
            onChange={(event) => setForm({ ...form, address: event.target.value })}
            placeholder="House or unit, street, barangay, city"
          />
        </Field>

        {/*
          * Two fields rather than one line of free text. This is read in a
          * hurry by somebody who needs to know who they are about to ring.
          */}
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="In an emergency, call" hint="Their name and how they are related.">
            <Input
              value={form.emergencyContactName}
              onChange={(event) => setForm({ ...form, emergencyContactName: event.target.value })}
              placeholder="Maria Asis (mother)"
            />
          </Field>
          <Field label="Emergency contact number">
            <Input
              value={form.emergencyContact}
              onChange={(event) => setForm({ ...form, emergencyContact: event.target.value })}
              inputMode="tel"
              placeholder="0917 000 0000"
            />
          </Field>
        </div>

        {/*
          * What the rota opens on for this person. Not a contract — every
          * shift can still be changed — but scheduling a week used to mean
          * correcting the same two times seven days running.
          */}
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Usually starts" hint="Used to fill in a new shift on the rota.">
            <Input
              type="time"
              value={form.defaultShiftStart}
              onChange={(event) => setForm({ ...form, defaultShiftStart: event.target.value })}
            />
          </Field>
          <Field label="Usually ends">
            <Input
              type="time"
              value={form.defaultShiftEnd}
              onChange={(event) => setForm({ ...form, defaultShiftEnd: event.target.value })}
            />
          </Field>
        </div>

        <Field label="How they are paid">
          <Select value={form.payType} onChange={(event) => setForm({ ...form, payType: event.target.value as Employee['payType'] })}>
            <option value="monthly">Monthly salary</option>
            <option value="daily">Daily rate</option>
            <option value="hourly">Hourly rate</option>
            <option value="per_shift">Per shift worked</option>
            <option value="piece_rate">Per piece produced or delivered</option>
          </Select>
        </Field>

        {form.payType === 'monthly' && (
          <Field label="Monthly salary">
            <MoneyInput value={form.monthlySalary} onChange={(value) => setForm({ ...form, monthlySalary: value })} />
          </Field>
        )}
        {form.payType === 'per_shift' && (
          <Field label="Rate per shift">
            <MoneyInput value={form.shiftRate} onChange={(value) => setForm({ ...form, shiftRate: value })} />
          </Field>
        )}
        {form.payType === 'daily' && (
          <Field label="Daily rate">
            <MoneyInput value={form.dailyRate} onChange={(value) => setForm({ ...form, dailyRate: value })} />
          </Field>
        )}
        {form.payType === 'hourly' && (
          <Field label="Hourly rate">
            <MoneyInput value={form.hourlyRate} onChange={(value) => setForm({ ...form, hourlyRate: value })} />
          </Field>
        )}

        {form.payType === 'piece_rate' && (
          <div>
            <p className="mb-2 text-sm font-medium">Piece rates</p>
            <div className="space-y-2">
              {payItems.map((item, index) => (
                <div key={index} className="grid grid-cols-12 items-end gap-2">
                  <div className="col-span-5">
                    <Input
                      placeholder="Baked loaves"
                      value={item.label}
                      onChange={(event) => setPayItems((c) => c.map((p, i) => (i === index ? { ...p, label: event.target.value } : p)))}
                    />
                  </div>
                  <div className="col-span-3">
                    <Input
                      placeholder="loaves"
                      value={item.unitLabel}
                      onChange={(event) => setPayItems((c) => c.map((p, i) => (i === index ? { ...p, unitLabel: event.target.value } : p)))}
                    />
                  </div>
                  <div className="col-span-3">
                    <MoneyInput value={item.rate} onChange={(rate) => setPayItems((c) => c.map((p, i) => (i === index ? { ...p, rate } : p)))} />
                  </div>
                  <button className="col-span-1 pb-3" onClick={() => setPayItems((c) => c.filter((_, i) => i !== index))} aria-label="Remove rate">
                    <Trash2 className="size-4 text-[var(--color-ink-faint)] hover:text-[var(--color-danger)]" />
                  </button>
                </div>
              ))}
              <Button
                size="sm"
                icon={<Plus className="size-4" />}
                onClick={() =>
                  setPayItems((current) => [...current, { id: undefined, label: '', unitLabel: 'pcs', rate: 0 }])
                }
              >
                Add a rate
              </Button>
            </div>
          </div>
        )}
      </div>

      {positionsOpen && (
        <PositionsModal onClose={() => setPositionsOpen(false)} />
      )}

      {leaving && employee && (
        <OffboardModal
          employee={employee}
          onCancel={() => setLeaving(false)}
          onDone={() => {
            setLeaving(false);
            refresh();
            onClose();
          }}
        />
      )}
    </Modal>
  );
}

/**
 * Recording that somebody has left.
 *
 * Deliberately not a delete. Their payslips name them, the days counted
 * against their rates are the workings behind those payslips, and a year later
 * "who was on that week" is a question somebody will ask. They come off the
 * team list, the rota and the payslip picker; everything else stays.
 */
function OffboardModal({
  employee,
  onCancel,
  onDone,
}: {
  employee: EmployeeWithPay;
  onCancel: () => void;
  onDone: () => void;
}) {
  const toast = useToast();
  const [endedOn, setEndedOn] = useState(shopDate());
  const [reason, setReason] = useState('');

  const offboard = useMutation({
    mutationFn: () => api.staff.offboard(employee.id, { endedOn, reason }),
    onSuccess: (saved) => {
      toast.push({
        tone: 'ok',
        title: `${saved.displayName} marked as having left`,
        body: 'Their record and payslips are kept, under Former on the Team tab.',
      });
      onDone();
    },
    onError: (error: Error) => toast.push({ tone: 'danger', title: 'Could not do that', body: error.message }),
  });

  return (
    <Modal
      open
      onClose={onCancel}
      title={`${employee.displayName} has left`}
      description="Their record stays on file. You can put them back on the team at any time."
      footer={
        <>
          <Button variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
          <Button variant="primary" loading={offboard.isPending} onClick={() => offboard.mutate()}>
            Mark as left
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Field label="Last day">
          <Input type="date" value={endedOn} onChange={(event) => setEndedOn(event.target.value)} />
        </Field>
        <Field label="Reason" hint="Optional, and only you will read it.">
          <Input
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder="Resigned, end of contract, dismissed…"
          />
        </Field>
      </div>
    </Modal>
  );
}
